(() => {
  if (customElements.get('minaco-instagram-reels')) return;

  class MinacoInstagramReels extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.track = this.querySelector('[data-reels-track]');
      this.status = this.querySelector('[data-reels-status]');
      this.prevButton = this.querySelector('[data-reels-prev]');
      this.nextButton = this.querySelector('[data-reels-next]');
      this.modal = this.querySelector('[data-reels-modal]');
      this.modalVideo = this.querySelector('[data-reels-modal-video]');
      this.modalClose = this.querySelector('[data-reels-modal-close]');

      this.apiKey = (this.dataset.apiKey || '').trim();
      this.apiVersion = this.dataset.apiVersion === 'v5' ? 'v5' : 'v6';
      this.account = (this.dataset.account || '').trim();
      this.maxReels = Math.min(30, Math.max(1, Number(this.dataset.maxReels) || 30));
      this.requestLimit = Math.min(90, Math.max(30, Number(this.dataset.requestLimit) || 60));

      this.posts = [];
      this.activeButton = null;
      this.activeIndex = -1;
      this.modalIndex = -1;
      this.lastModalTrigger = null;
      this.idlePlaybackTimer = null;
      this.pendingPlaybackIndex = -1;
      this.playAttemptSerial = 0;
      this.fetchAbortController = new AbortController();
      this.scrollAnimationFrame = null;
      this.animationStartTime = 0;
      this.scrollStart = 0;
      this.scrollTarget = 0;
      this.scrollDuration = 920;
      this.previousDocumentOverflow = '';
      this.previousBodyOverflow = '';

      this.onPrevClick = () => this.scrollByPage(-1);
      this.onNextClick = () => this.scrollByPage(1);
      this.onTrackScroll = this.handleTrackScroll.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onModalClose = this.closeModal.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.track?.addEventListener('scroll', this.onTrackScroll, { passive: true });
      this.modalClose?.addEventListener('click', this.onModalClose);
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('keydown', this.onKeyDown);

      this.loadFeed();
    }

    disconnectedCallback() {
      this.fetchAbortController?.abort();
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.track?.removeEventListener('scroll', this.onTrackScroll);
      this.modalClose?.removeEventListener('click', this.onModalClose);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('keydown', this.onKeyDown);
      this.cancelIdlePlayback();
      this.pendingPlaybackIndex = -1;
      this.cancelScrollAnimation();
      this.stopActiveReel(true);
      this.teardownModal(false);
    }

    async loadFeed(attempt = 0) {
      if (!this.track) return;

      if (!this.apiKey) {
        this.renderEmptyState(
          'Connect Instafeed and paste the Feed API key in this section to load Instagram Reels.'
        );
        return;
      }

      const endpoint = `https://instafeed.nfcube.com/feed/${this.apiVersion}`;
      const params = new URLSearchParams({
        limit: String(this.requestLimit),
        account: this.account,
        fu: '0',
        fid: '0',
        locale: document.documentElement.lang || 'en'
      });

      try {
        const response = await fetch(`${endpoint}?${params.toString()}`, {
          method: 'GET',
          headers: {
            'X-API-Key': this.apiKey,
            Accept: 'application/json'
          },
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          signal: this.fetchAbortController.signal
        });

        if (!response.ok) {
          throw new Error(`Instafeed HTTP ${response.status}`);
        }

        const payload = await response.json();
        const code = Number(payload?.meta?.code || response.status);

        if (code === 202 && attempt < 4) {
          await this.wait(1400 + attempt * 600);
          return this.loadFeed(attempt + 1);
        }

        if (code !== 200) {
          throw new Error(payload?.meta?.error_message || `Instafeed API code ${code}`);
        }

        const allPosts = Array.isArray(payload?.data) ? payload.data : [];
        this.posts = allPosts
          .filter((post) => post?.type === 'video' && this.getPosterUrl(post) && this.getVideoUrl(post))
          .slice(0, this.maxReels);

        if (!this.posts.length) {
          this.renderEmptyState(
            'No playable Reels were returned. In Instafeed, enable Own posts + Include all Reels and save the feed.'
          );
          return;
        }

        this.renderPosts();
      } catch (error) {
        if (error?.name === 'AbortError') return;

        console.error('[MINACO Instagram Reels]', error);
        this.renderEmptyState(
          'Instagram Reels could not be loaded. Check the Feed API key and the selected Instafeed connection type.'
        );
      }
    }

    renderPosts() {
      this.track.replaceChildren();

      const fragment = document.createDocumentFragment();
      this.posts.forEach((post, index) => {
        fragment.appendChild(this.createSlide(post, index));
      });

      this.track.appendChild(fragment);
      this.status.hidden = true;

      requestAnimationFrame(() => {
        this.updateArrows();
        window.setTimeout(() => this.playFirstVisibleReel(), 60);
      });
    }

    createSlide(post, index) {
      const article = document.createElement('article');
      article.className = 'minaco-instagram-reels__slide';
      article.dataset.reelIndex = String(index);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'minaco-instagram-reels__media-button';
      button.setAttribute('aria-label', `Open Instagram reel ${index + 1}`);
      button.dataset.reelIndex = String(index);
      button.dataset.videoUrl = this.getVideoUrl(post);
      button.dataset.posterUrl = this.getPosterUrl(post);
      button.dataset.alt = post?.alt_text || this.captionToAlt(post?.caption?.text) || `Instagram reel ${index + 1}`;

      const image = document.createElement('img');
      image.className = 'minaco-instagram-reels__poster';
      image.src = button.dataset.posterUrl;
      image.alt = button.dataset.alt;
      image.loading = index < 3 ? 'eager' : 'lazy';
      image.decoding = 'async';

      button.appendChild(image);
      button.addEventListener('click', () => this.openModal(index, button));

      article.appendChild(button);
      return article;
    }

    async playInlineReel(index) {
      if (document.hidden || this.isModalOpen() || !this.posts.length) return;

      const normalizedIndex = ((Number(index) % this.posts.length) + this.posts.length) % this.posts.length;
      const button = this.track?.querySelector(`.minaco-instagram-reels__media-button[data-reel-index="${normalizedIndex}"]`);
      if (!button) return;

      this.cancelIdlePlayback();
      this.pendingPlaybackIndex = -1;

      if (this.activeButton && this.activeButton !== button) {
        this.resetButton(this.activeButton);
      }

      let video = button.querySelector('video');
      if (!video) {
        video = document.createElement('video');
        video.className = 'minaco-instagram-reels__video';
        video.playsInline = true;
        video.autoplay = true;
        video.preload = 'auto';
        video.controls = false;
        video.loop = false;
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.poster = button.dataset.posterUrl;
        video.src = button.dataset.videoUrl;

        video.addEventListener('ended', () => {
          if (
            this.activeButton === button &&
            this.activeIndex === normalizedIndex &&
            !this.isModalOpen() &&
            !document.hidden
          ) {
            this.advanceInlinePlayback(normalizedIndex);
          }
        });

        const poster = button.querySelector('.minaco-instagram-reels__poster');
        if (poster) poster.replaceWith(video);
        else button.prepend(video);
      }

      this.activeButton = button;
      this.activeIndex = normalizedIndex;
      button.classList.add('is-playing');

      if (video.ended || video.currentTime >= video.duration) {
        try { video.currentTime = 0; } catch (_error) {}
      }

      const attemptId = ++this.playAttemptSerial;

      try {
        await video.play();
      } catch (_error) {
        if (this.activeButton !== button || attemptId !== this.playAttemptSerial) return;

        const retryPlayback = () => {
          if (
            this.activeButton !== button ||
            attemptId !== this.playAttemptSerial ||
            this.isModalOpen() ||
            document.hidden ||
            !video.isConnected
          ) return;

          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
          video.play().catch(() => {});
        };

        if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
          video.addEventListener('canplay', retryPlayback, { once: true });
        } else {
          window.setTimeout(retryPlayback, 80);
        }
      }
    }

    playFirstVisibleReel() {
      const index = this.getFirstVisibleIndex();
      if (index >= 0) this.playInlineReel(index);
    }

    getFirstVisibleIndex() {
      if (!this.track) return -1;

      const trackRect = this.track.getBoundingClientRect();
      const slides = Array.from(this.track.querySelectorAll('.minaco-instagram-reels__slide'));
      let fallbackIndex = -1;
      let fallbackVisibility = 0;

      for (const slide of slides) {
        const rect = slide.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left));
        const visibility = rect.width > 0 ? visibleWidth / rect.width : 0;
        const index = Number(slide.dataset.reelIndex);

        if (visibility >= 0.5) return index;
        if (visibility > fallbackVisibility) {
          fallbackVisibility = visibility;
          fallbackIndex = index;
        }
      }

      return fallbackIndex;
    }

    isReelFullyVisible(index) {
      if (!this.track) return false;

      const slide = this.track.querySelector(`.minaco-instagram-reels__slide[data-reel-index="${index}"]`);
      if (!slide) return false;

      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const epsilon = 2;

      return slideRect.left >= trackRect.left - epsilon && slideRect.right <= trackRect.right + epsilon;
    }

    getScrollTargetForReel(index) {
      if (!this.track) return 0;

      const slide = this.track.querySelector(`.minaco-instagram-reels__slide[data-reel-index="${index}"]`);
      if (!slide) return this.track.scrollLeft;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (index === 0) return 0;

      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      let target = this.track.scrollLeft;

      if (slideRect.right > trackRect.right) {
        target += slideRect.right - trackRect.right;
      } else if (slideRect.left < trackRect.left) {
        target -= trackRect.left - slideRect.left;
      }

      return Math.min(maxScroll, Math.max(0, target));
    }

    advanceInlinePlayback(currentIndex) {
      if (!this.posts.length || this.isModalOpen() || document.hidden) return;

      const nextIndex = (currentIndex + 1) % this.posts.length;
      this.stopActiveReel(true);

      if (this.isReelFullyVisible(nextIndex)) {
        requestAnimationFrame(() => this.playInlineReel(nextIndex));
        return;
      }

      this.scrollToReelAndPlay(nextIndex);
    }

    scrollToReelAndPlay(index) {
      if (!this.track || !this.posts[index] || this.isModalOpen()) return;

      this.cancelIdlePlayback();
      this.stopActiveReel(true);
      this.pendingPlaybackIndex = index;

      const target = this.getScrollTargetForReel(index);
      this.animateScrollTo(target, () => {
        if (this.pendingPlaybackIndex !== index || this.isModalOpen() || document.hidden) return;
        this.pendingPlaybackIndex = -1;
        this.playInlineReel(index);
      });
    }

    resetButton(button) {
      if (!button) return;

      const video = button.querySelector('video');
      if (video) {
        video.pause();

        const image = document.createElement('img');
        image.className = 'minaco-instagram-reels__poster';
        image.src = button.dataset.posterUrl;
        image.alt = button.dataset.alt || 'Instagram reel';
        image.loading = 'lazy';
        image.decoding = 'async';

        video.removeAttribute('src');
        video.load();
        video.replaceWith(image);
      }

      button.classList.remove('is-playing');

      if (this.activeButton === button) {
        this.activeButton = null;
        this.activeIndex = -1;
      }
    }

    stopActiveReel(unload = false) {
      if (!this.activeButton) return;

      const button = this.activeButton;
      const video = button.querySelector('video');
      video?.pause();
      button.classList.remove('is-playing');

      if (unload) {
        this.resetButton(button);
      }
    }

    handleTrackScroll() {
      this.updateArrows();
      if (this.isModalOpen()) return;

      if (this.pendingPlaybackIndex >= 0) return;

      this.stopActiveReel(true);
      this.scheduleIdlePlayback(3000);
    }

    handleResize() {
      this.updateArrows();
      if (!this.activeButton && !this.isModalOpen()) {
        this.scheduleIdlePlayback(300);
      }
    }

    scheduleIdlePlayback(delay = 3000) {
      this.cancelIdlePlayback();
      this.idlePlaybackTimer = window.setTimeout(() => {
        this.idlePlaybackTimer = null;
        this.playFirstVisibleReel();
      }, delay);
    }

    cancelIdlePlayback() {
      if (this.idlePlaybackTimer) {
        window.clearTimeout(this.idlePlaybackTimer);
        this.idlePlaybackTimer = null;
      }
    }

    openModal(index, trigger) {
      if (!this.modal || !this.modalVideo || !this.posts[index]) return;

      this.cancelIdlePlayback();
      this.pendingPlaybackIndex = -1;
      this.cancelScrollAnimation();
      this.stopActiveReel(true);
      this.modalIndex = index;
      this.lastModalTrigger = trigger || null;

      this.modalVideo.pause();
      this.modalVideo.removeAttribute('poster');
      this.modalVideo.loop = true;
      this.modalVideo.controls = false;
      this.modalVideo.autoplay = true;
      this.modalVideo.muted = false;
      this.modalVideo.playsInline = true;
      this.modalVideo.preload = 'auto';
      this.modalVideo.setAttribute('autoplay', '');
      this.modalVideo.setAttribute('playsinline', '');
      this.modalVideo.setAttribute('webkit-playsinline', '');
      this.modalVideo.src = this.getVideoUrl(this.posts[index]);
      this.modalVideo.load();

      this.lockPageScroll();
      this.modal.hidden = false;
      this.modalClose?.focus({ preventScroll: true });

      const playback = this.modalVideo.play();
      if (playback?.catch) {
        playback.catch(() => {
          this.modalVideo.muted = true;
          this.modalVideo.play().catch(() => {});
        });
      }
    }

    closeModal() {
      if (!this.isModalOpen()) return;

      const nextIndex = this.posts.length
        ? (this.modalIndex + 1) % this.posts.length
        : -1;

      this.teardownModal(true);

      if (nextIndex >= 0 && !document.hidden) {
        requestAnimationFrame(() => {
          if (this.isReelFullyVisible(nextIndex)) this.playInlineReel(nextIndex);
          else this.scrollToReelAndPlay(nextIndex);
        });
      }
    }

    teardownModal(restoreFocus = true) {
      if (!this.modal || !this.modalVideo) return;

      const wasOpen = !this.modal.hidden;
      this.modalVideo.pause();
      this.modalVideo.removeAttribute('src');
      this.modalVideo.removeAttribute('poster');
      this.modalVideo.load();
      this.modal.hidden = true;

      if (wasOpen) this.unlockPageScroll();

      if (restoreFocus && this.lastModalTrigger?.isConnected) {
        this.lastModalTrigger.focus({ preventScroll: true });
      }

      this.modalIndex = -1;
      this.lastModalTrigger = null;
    }

    isModalOpen() {
      return Boolean(this.modal && !this.modal.hidden);
    }

    handleKeyDown(event) {
      if (!this.isModalOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        this.modalClose?.focus({ preventScroll: true });
      }
    }

    lockPageScroll() {
      this.previousDocumentOverflow = document.documentElement.style.overflow;
      this.previousBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    unlockPageScroll() {
      document.documentElement.style.overflow = this.previousDocumentOverflow;
      document.body.style.overflow = this.previousBodyOverflow;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.cancelIdlePlayback();
        this.stopActiveReel(true);
        this.modalVideo?.pause();
        return;
      }

      if (this.isModalOpen()) {
        this.modalVideo?.play().catch(() => {});
      } else {
        this.scheduleIdlePlayback(300);
      }
    }

    getPosterUrl(post) {
      return (
        post?.images?.standard_resolution?.url ||
        post?.images?.low_resolution?.url ||
        post?.thumbnail_url ||
        ''
      );
    }

    getVideoUrl(post) {
      return (
        post?.videos?.standard_resolution?.url ||
        post?.videos?.low_resolution?.url ||
        ''
      );
    }

    captionToAlt(caption) {
      if (!caption) return '';
      return String(caption).replace(/\s+/g, ' ').trim().slice(0, 140);
    }

    scrollByPage(direction) {
      if (!this.track) return;

      this.pendingPlaybackIndex = -1;
      this.stopActiveReel(true);
      this.cancelIdlePlayback();

      const firstSlide = this.track.querySelector('.minaco-instagram-reels__slide');
      if (!firstSlide) return;

      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const step = firstSlide.getBoundingClientRect().width + gap;
      if (step <= 0) return;

      const currentPosition = this.track.scrollLeft / step;
      const currentIndex = direction > 0
        ? Math.floor(currentPosition + 0.04)
        : Math.ceil(currentPosition - 0.04);
      const targetIndex = currentIndex + direction;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const target = Math.min(maxScroll, Math.max(0, targetIndex * step));

      this.animateScrollTo(target);
    }

    animateScrollTo(target, onComplete = null) {
      if (!this.track) return;

      this.cancelScrollAnimation();
      const start = this.track.scrollLeft;
      const distance = target - start;

      if (Math.abs(distance) < 1) {
        this.track.scrollLeft = target;
        this.updateArrows();
        if (typeof onComplete === 'function') onComplete();
        else this.scheduleIdlePlayback(3000);
        return;
      }

      this.scrollStart = start;
      this.scrollTarget = target;
      this.animationStartTime = performance.now();
      this.track.classList.add('is-programmatic-scrolling');

      const step = (now) => {
        const elapsed = now - this.animationStartTime;
        const progress = Math.min(1, elapsed / this.scrollDuration);
        const eased = this.easeInOutCubic(progress);
        this.track.scrollLeft = this.scrollStart + (distance * eased);
        this.updateArrows();

        if (progress < 1) {
          this.scrollAnimationFrame = window.requestAnimationFrame(step);
        } else {
          this.track.scrollLeft = this.scrollTarget;
          this.scrollAnimationFrame = null;
          window.requestAnimationFrame(() => {
            this.track?.classList.remove('is-programmatic-scrolling');
            this.updateArrows();
            if (typeof onComplete === 'function') onComplete();
            else this.scheduleIdlePlayback(3000);
          });
        }
      };

      this.scrollAnimationFrame = window.requestAnimationFrame(step);
    }

    cancelScrollAnimation() {
      if (this.scrollAnimationFrame) {
        window.cancelAnimationFrame(this.scrollAnimationFrame);
        this.scrollAnimationFrame = null;
      }
      this.track?.classList.remove('is-programmatic-scrolling');
    }

    easeInOutCubic(value) {
      return value < 0.5
        ? 4 * value * value * value
        : 1 - Math.pow(-2 * value + 2, 3) / 2;
    }

    updateArrows() {
      if (!this.track || !this.prevButton || !this.nextButton) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const current = this.track.scrollLeft;
      const epsilon = 3;

      this.prevButton.disabled = current <= epsilon;
      this.nextButton.disabled = current >= maxScroll - epsilon;
    }

    renderEmptyState(message) {
      this.cancelIdlePlayback();
      this.stopActiveReel(true);
      this.track?.replaceChildren();

      if (this.status) {
        this.status.textContent = message;
        this.status.hidden = false;
      }

      if (this.prevButton) this.prevButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
    }

    wait(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }
  }

  customElements.define('minaco-instagram-reels', MinacoInstagramReels);
})();
